import type {
  ChatInput, ConversazioneBreve, ConversazioneSalvata, EventiChat, ParteRisposta, SchedaLettaDto,
} from '@masamune/shared';
import { api, ApiError, leggiFlusso } from '../../lib/api';
import { testoPiano } from './rivela';

/**
 * La chat di MUNE in streaming (29/10/2026, fetta 2B della Fase 2,
 * os-planning/ai/fase-2-progetto.md, «Contratti»). La domanda va a POST /ai/chat con l'id
 * della conversazione e la pagina aperta; la storia la tiene il server (AiConversation,
 * AiMessage), il browser non la manda più. La risposta arriva a eventi: `conversazione`
 * sempre per primo, poi `letto`, `stato`, `riquadro`, `testo`, `reazione`, `righe` nell'ordine
 * in cui il server li produce, e sempre per ultimo `fine`.
 *
 * Qui anche le conversazioni salvate (30 giorni): l'elenco, la lettura, l'eliminazione.
 * La domanda parte solo da Invio o dal tasto, mai da un effetto o da una query: costa, e in
 * locale React.StrictMode la farebbe partire due volte.
 *
 * Dalla fetta 2F (29/10/2026, os-planning/ai/fase-2f-allegati.md) la domanda porta anche gli id
 * delle foto e dei PDF già caricati a parte (allegatiApi.ts, POST /ai/allegati): il corpo resta
 * un JSON piccolo, i byte dei file qui non passano mai.
 */

/** Un evento del flusso col suo nome: con `switch (e.nome)` i dati hanno già il tipo giusto. */
export type EventoChat = { [K in keyof EventiChat]: { nome: K; dati: EventiChat[K] } }[keyof EventiChat];

/**
 * I nomi che il pannello conosce: un evento con un altro nome (di una versione dopo) si salta.
 * `allegati` dalla 2F: gli allegati che MUNE non ha letto, usciti dalla conversazione.
 */
const EVENTI: ReadonlySet<string> = new Set<keyof EventiChat>([
  'conversazione', 'avviso', 'letto', 'stato', 'riquadro', 'testo', 'reazione', 'righe', 'azione', 'conferma', 'mail', 'allegati', 'errore', 'fine',
]);

/**
 * Il flusso si è chiuso senza `fine`: la rete è caduta a metà, o il server si è riavviato
 * («Parole a schermo», errori a flusso partito). leggiFlusso (lib/api.ts) in quel caso si
 * chiude bene lo stesso: è inviaDomanda che se ne accorge.
 */
export const FRASE_CONNESSIONE_INTERROTTA = 'La connessione si è interrotta: riprova.';

/**
 * Manda la domanda e passa a `onEvento` ogni evento intero, man mano che arriva. Gli errori
 * prima del flusso (400, 402, 409, 503 in JSON) arrivano come ApiError con la frase dell'API;
 * un flusso finito senza `fine` lancia ApiError(502, 'FLUSSO_INTERROTTO') con
 * FRASE_CONNESSIONE_INTERROTTA. Interrotta col `signal` (Nuova conversazione, un'altra
 * conversazione aperta) finisce in silenzio, senza errore.
 *
 * Con degli allegati (2F) il testo può essere vuoto; gli errori degli allegati della domanda
 * arrivano anche loro prima del flusso, con gli id dei pezzi in `details.allegati` (le frasi
 * col nome le sceglie allegatiTesto.ts, fraseDomandaRimessa).
 */
export async function inviaDomanda(p: {
  conversazioneId: string | null;
  testo: string;
  /** 2F: gli id delle foto e dei PDF già sul server; vuoto senza allegati. */
  allegati: string[];
  pagina: string;
  signal: AbortSignal;
  onEvento: <K extends keyof EventiChat>(nome: K, dati: EventiChat[K]) => void;
}): Promise<void> {
  let fine = false;
  // chatInput (packages/shared): la pagina al massimo di 300 caratteri. Senza allegati il corpo
  // resta quello di prima della 2F, uguale byte per byte (fase-2f-allegati.md, «Perché resta sano»).
  const corpo: ChatInput = {
    conversazioneId: p.conversazioneId,
    testo: p.testo,
    pagina: p.pagina.slice(0, 300),
    ...(p.allegati.length ? { allegati: p.allegati } : {}),
  };
  await leggiFlusso('/ai/chat', {
    method: 'POST',
    body: corpo,
    signal: p.signal,
    onEvento: (nome, dati) => {
      if (!EVENTI.has(nome)) return;
      if (nome === 'fine') fine = true;
      p.onEvento(nome as keyof EventiChat, dati as EventiChat[keyof EventiChat]);
    },
  });
  if (!fine && !p.signal.aborted) throw new ApiError(502, 'FLUSSO_INTERROTTO', FRASE_CONNESSIONE_INTERROTTA);
}

/** «Conversazioni» del menu ⋯: le proprie, toccate negli ultimi 30 giorni, le più recenti prima. */
export const conversazioniQuery = {
  queryKey: ['ai', 'conversazioni'] as const,
  queryFn: () => api.get<ConversazioneBreve[]>('/ai/conversazioni'),
  staleTime: 15_000 as const,
};

/** Una conversazione salvata, coi turni come li ha visti chi chiedeva (riquadri compresi). */
export function leggiConversazione(id: string): Promise<ConversazioneSalvata> {
  return api.get<ConversazioneSalvata>(`/ai/conversazioni/${encodeURIComponent(id)}`);
}

/** Sparisce per sempre: le modifiche fatte da MUNE restano nel registro. */
export function eliminaConversazione(id: string): Promise<void> {
  return api.delete<void>(`/ai/conversazioni/${encodeURIComponent(id)}`);
}

/**
 * Quello che si legge di una risposta, per i lettori di schermo a fine turno: i testi senza
 * asterischi, il titolo di ogni riquadro e gli avvisi, nell'ordine in cui sono arrivati. I
 * numeri no: si leggono nel riquadro, riga per riga.
 */
export function testoDaLeggere(parti: ParteRisposta[]): string {
  return parti
    .map((p) => {
      switch (p.tipo) {
        case 'testo': return testoPiano(p.testo).trim();
        case 'riquadro': return `${p.riquadro.titolo}.`;
        case 'avviso': return p.testo;
        default: return '';
      }
    })
    .filter(Boolean)
    .join(' ');
}

/**
 * Le schede lette nel turno: l'evento `letto` arriva dopo il contesto e di nuovo quando uno
 * strumento legge un cliente o un progetto con la scheda. Si tengono tutte, una volta sola.
 */
export function unisciLette(prima: SchedaLettaDto[] | undefined, nuove: SchedaLettaDto[]): SchedaLettaDto[] {
  const tutte = [...(prima ?? [])];
  for (const s of nuove) {
    const i = tutte.findIndex((x) => x.tipo === s.tipo && x.id === s.id);
    if (i < 0) tutte.push(s);
    else tutte[i] = s;
  }
  return tutte;
}
